import { BaseApiClient } from './baseApiClient';
import { FetchHilSubmitClient, DevMockHilSubmitClient, HIL_DEV_CONFIG } from '@bizfirst/hil-core';
import type { IHilSubmitClient, HilSubmitResult } from '@bizfirst/hil-core';
import type { HilPresentationReplyEnvelope } from '@bizfirst/hil-types';

/**
 * Exposes the protected HTTP helpers of BaseApiClient (buildUrl/getHeaders/postQuery)
 * as public members via composition, so InboxItemHilSubmitClient can reuse them without
 * extending BaseApiClient — extending it would make this class inherit BaseApiClient's
 * protected `post<T>(endpoint, body?, options?): Promise<ApiResponse<T>>`, which collides
 * with (and is structurally incompatible with) the `post(url, payload): Promise<HilSubmitResult>`
 * member required by IHilSubmitClient below.
 */
class InboxActionHttpClient extends BaseApiClient {
  public buildUrl(endpoint: string): string {
    return super.buildUrl(endpoint);
  }

  public getHeaders(customHeaders?: Record<string, string>): HeadersInit {
    return super.getHeaders(customHeaders);
  }

  public postQuery<T>(endpoint: string, body?: any) {
    return super.postQuery<T>(endpoint, body);
  }
}

/**
 * Submit client for HIL items loaded from the database via the inbox API.
 *
 * When the URL matches `/api/v1/ai/inbox-items/{id}/act`, maps the v1.3
 * HilPresentationReplyEnvelope to the backend ActOnInboxItemRequest format.
 * For any other URL, falls back to FetchHilSubmitClient (or mock in dummy mode).
 */
export class InboxItemHilSubmitClient implements IHilSubmitClient {
  private readonly _http = new InboxActionHttpClient();
  private readonly _fallback: IHilSubmitClient = HIL_DEV_CONFIG.isDummyEnabled
    ? new DevMockHilSubmitClient()
    : new FetchHilSubmitClient();

  async post(url: string, payload: HilPresentationReplyEnvelope): Promise<HilSubmitResult> {
    // Handle /api/hil/respond/{sessionId} — the ResumeCallbackURL baked into every HIL envelope by the backend.
    // Must use buildUrl() to prepend the API base (https://localhost:10001) and getHeaders() for auth/tenant headers.
    // The bare relative URL would otherwise resolve to the Vite dev server (port 6005).
    if (/\/api\/hil\/respond\/\d+/.test(url)) {
      try {
        const fullUrl = this._http.buildUrl(url);
        const response = await fetch(fullUrl, {
          method: 'POST',
          headers: this._http.getHeaders({ 'Content-Type': 'application/json' }) as Record<string, string>,
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          return { ok: false, statusCode: response.status, error: `HTTP ${response.status}` };
        }
        return { ok: true, statusCode: response.status };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : String(err) };
      }
    }

    const match = url.match(/\/api\/v1\/ai\/inbox-items\/(\d+)\/act/);
    if (!match) {
      return this._fallback.post(url, payload);
    }

    const inboxItemId = parseInt(match[1], 10);
    const body = {
      InboxItemID: { ID: inboxItemId },
      ActorID: payload.Responders?.[0]?.ActorID ?? '',
      Decision: payload.Decision ?? '',
      DecisionReasonText: payload.Payload?.Reason ?? payload.Payload?.Comment,
      AdditionalData: payload.Payload?.Data as Record<string, object> | undefined,
      ActedAt: payload.DecidedAt,
    };

    const resp = await this._http.postQuery<void>(
      `/api/v1/ai/inbox-items/${inboxItemId}/act`,
      body
    );
    return { ok: resp.success, error: resp.success ? undefined : resp.error?.message };
  }
}

export const inboxItemHilSubmitClient = new InboxItemHilSubmitClient();
